import { Tooltip } from '@chain-zen/ui'
import React from 'react'

import { dateTimeAgo, formatFetchedAndLastSeenTimeParts } from '../../utils/datetime-utils'
import { formatLocationText } from '../../utils/format-location-text'

interface ITrackingTimeDisplayProps {
  /**
   * The ISO datetime when the location was last fetched from the tracking source
   */
  lastLocationFetchedAt?: string | null
  /**
   * Optional city from the last known location
   */
  lastLocationCity?: string | null
  /**
   * Optional state code from the last known location
   */
  lastLocationStateCode?: string | null
  /**
   * The timestamp of the last location
   */
  lastLocationTimestamp: string
  /**
   * The reason why the location was filtered (if applicable)
   */
  filterReason?: string | null
  /**
   * Whether to show a separator before the time display
   */
  showSeparator?: boolean
  /**
   * Custom wrapper element (default: fragment)
   */
  wrapper?: 'p' | 'div' | 'fragment'
  /**
   * Additional className for the wrapper
   */
  className?: string
  /**
   * Whether to show the live pulse indicator when location is present
   */
  showLiveIndicator?: boolean
}

const BroadcastIcon = ({
  className,
  style,
  'data-testid': testID
}: {
  className?: string
  style?: React.CSSProperties
  'data-testid'?: string
}) => (
  <svg
    className={className}
    style={style}
    data-testid={testID}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9"></path>
    <path d="M7.8 16.2c-2.3-2.3-2.3-6.1 0-8.5"></path>
    <circle cx="12" cy="12" r="2" fill="currentColor"></circle>
    <path d="M16.2 7.8c2.3 2.3 2.3 6.1 0 8.5"></path>
    <path d="M19.1 4.9C23 8.8 23 15.1 19.1 19"></path>
  </svg>
)

const LocationVerifiedTooltip = ({
  checkedAgo,
  filterReason
}: {
  checkedAgo: string
  filterReason?: string | null
}) => (
  <div className="flex flex-col gap-1 items-center text-center w-full">
    <span className="font-semibold">Location Verified</span>
    {!!filterReason && <span className="opacity-70">{filterReason}</span>}
    <span className="opacity-70">Checked {checkedAgo}</span>
  </div>
)

const PulseIndicator = ({ isFreshLocation }: { isFreshLocation: boolean }) => (
  <span className="relative inline-flex h-2 w-2 flex-shrink-0" aria-hidden={true}>
    <span
      className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 ${
        isFreshLocation ? 'bg-green-500/60 dark:bg-green-400/50' : 'bg-yellow-500/70 dark:bg-yellow-400/60'
      }`}
    />
    <span
      className={`relative inline-flex h-2 w-2 rounded-full ${
        isFreshLocation ? 'bg-green-500 dark:bg-green-400' : 'bg-yellow-500 dark:bg-yellow-400'
      }`}
    />
  </span>
)

/**
 * Reusable component for displaying tracking time information with optional location text.
 */
export const TrackingTimeDisplay = (props: ITrackingTimeDisplayProps) => {
  const {
    filterReason,
    lastLocationFetchedAt,
    lastLocationCity,
    lastLocationStateCode,
    lastLocationTimestamp,
    showSeparator = false,
    wrapper = 'fragment',
    className = '',
    showLiveIndicator = true
  } = props

  const { updatedText, lastSeenText } = formatFetchedAndLastSeenTimeParts(lastLocationFetchedAt, lastLocationTimestamp)

  const locationText = formatLocationText(lastLocationCity || undefined, lastLocationStateCode || undefined)
  const hasLocationText = locationText !== '-'
  const checkedAgo = lastLocationFetchedAt ? dateTimeAgo(lastLocationFetchedAt) : null

  const lastLocationAgeMs = Date.now() - new Date(lastLocationTimestamp).getTime()
  const hasRecentLocation = !Number.isNaN(lastLocationAgeMs) && lastLocationAgeMs >= 0
  const isFreshLocation = hasRecentLocation && lastLocationAgeMs <= 2 * 60 * 60 * 1000
  const showLocationVerifiedIcon =
    !!lastLocationFetchedAt && !!checkedAgo && lastLocationFetchedAt !== lastLocationTimestamp

  const displayText = hasLocationText ? `${lastSeenText} in ${locationText}` : lastSeenText
  const updatedDisplayText =
    updatedText && lastLocationFetchedAt ? `Updated ${dateTimeAgo(lastLocationFetchedAt)}` : updatedText

  const inlineContent = (
    <>
      {!!showSeparator && <span className="text-muted-foreground">•</span>}
      {!!showLiveIndicator && !!hasLocationText && <PulseIndicator isFreshLocation={isFreshLocation} />}
      <span className="inline-block">{displayText}</span>
      {!!showLocationVerifiedIcon && !!checkedAgo && (
        <Tooltip
          content={<LocationVerifiedTooltip checkedAgo={checkedAgo} filterReason={filterReason} />}
          className="bg-white text-black dark:bg-black dark:text-white text-xs"
          hideArrow={true}
        >
          <span className="inline-flex cursor-help">
            <BroadcastIcon
              className="h-[15px] w-[15px]"
              style={{
                color: isFreshLocation ? '#22c55e' : '#facc15',
                animation: 'hybrid-pulse 2.5s ease-in-out infinite'
              }}
              data-testid="location-verified-icon"
            />
          </span>
        </Tooltip>
      )}
      {!!updatedDisplayText && (
        <>
          <span className="text-muted-foreground">•</span>
          <span className="inline-block">{updatedDisplayText}</span>
        </>
      )}
      <style>{`
        @keyframes hybrid-pulse {
          0%, 100% {
            opacity: 0.7;
            transform: scale(1) rotate(0deg);
          }
          50% {
            opacity: 1;
            transform: scale(1.1) rotate(15deg);
          }
        }
      `}</style>
    </>
  )

  const baseContent = <span className="inline-flex items-center gap-1">{inlineContent}</span>
  const content =
    filterReason && !showLocationVerifiedIcon ? (
      <Tooltip content={filterReason}>
        <span className="inline-flex cursor-help items-center gap-1">{inlineContent}</span>
      </Tooltip>
    ) : (
      baseContent
    )

  if (wrapper === 'p') {
    return <p className={`inline-flex items-center gap-1 ${className}`}>{content}</p>
  }

  if (wrapper === 'div') {
    return <div className={`inline-flex items-center gap-1 ${className}`}>{content}</div>
  }

  return <>{content}</>
}
